/**
 * Display helpers for queued `downloadrange` (RFD) commands.
 *
 * When a passage or raw download (RFD FileType 0 or 3) is queued, the WebApi
 * also queues an RFD with FileType 99 ("stop any in-flight download") one tick
 * earlier, so the device ends any old download before the new one starts
 * (ProPipe.WebApi/Logic/AgmLogic.cs QueueCommand). The device protocol requires
 * a date range on every RFD, so the stop row carries start = end = the queue
 * time. Shown as a plain "Download Range", it looks like a second, zero-length
 * download (EN-1496). These helpers label it as what it is.
 */

/** RFD FileType that tells the device to stop any in-flight download. */
export const RFD_STOP_DOWNLOADS_FILE_TYPE = 99;

export const DOWNLOAD_RANGE_LABEL = 'Download Range';
export const STOP_DOWNLOADS_LABEL = 'Stop Downloads';
export const STOP_DOWNLOADS_PAYLOAD = 'Ends any current download';
/** Shown when a download row is missing a start or end date. */
export const MISSING_RANGE_PLACEHOLDER = '—';

export interface DownloadRangeParameters {
  FileType?: number | string | null;
  FileNamesStartDate?: string | null;
  FileNamesEndDate?: string | null;
}

/** True when the queued RFD is the automatic stop-downloads (FileType 99) row. */
export function isStopDownloadsCommand(
  parameters: DownloadRangeParameters | null | undefined
): boolean {
  if (parameters == null) return false;
  // The API sends FileType as a JSON number (byte?); also accept the exact
  // string '99' in case a caller serializes it as text. Nothing looser.
  const fileType = parameters.FileType;
  return fileType === RFD_STOP_DOWNLOADS_FILE_TYPE || fileType === '99';
}

/** Type column label for a queued `downloadrange` command. */
export function downloadRangeTypeLabel(
  parameters: DownloadRangeParameters | null | undefined
): string {
  return isStopDownloadsCommand(parameters)
    ? STOP_DOWNLOADS_LABEL
    : DOWNLOAD_RANGE_LABEL;
}

/**
 * Parameters column text for a queued `downloadrange` command: the time-of-day
 * range for a real download, or a short description for the stop row (whose
 * start = end range is a protocol placeholder, not a range to download).
 * The range slicing is unchanged from the original QueueCommands.showPayload;
 * a row missing either date shows a dash instead of a bare '/'.
 */
export function downloadRangePayload(
  parameters: DownloadRangeParameters | null | undefined
): string {
  if (parameters == null) return '';
  if (isStopDownloadsCommand(parameters)) return STOP_DOWNLOADS_PAYLOAD;

  const start = parameters.FileNamesStartDate;
  const end = parameters.FileNamesEndDate;
  if (!start || !end) return MISSING_RANGE_PLACEHOLDER;
  const a = start.substring(10, start.length - 7);
  const b = end.substring(10, end.length - 7);
  return a + '/' + b;
}
